import axios, { AxiosHeaders, AxiosError } from "axios";
import { auth } from "../firebaseConfig";

/**
 * Base URL normalization:
 * - ensures we always hit ".../api"
 * - avoids ending up with "/api/api"
 */
const RAW = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const API_BASE = RAW ? (RAW.endsWith("/api") ? RAW : `${RAW}/api`) : "/api";

const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
});

/* ----------------------------------------------------------------------------
 * Interceptors
 * --------------------------------------------------------------------------*/

// Small helper to set headers whether Axios uses plain object or AxiosHeaders
function setHeader(
  headers: AxiosHeaders | Record<string, any>,
  key: string,
  val: string
) {
  if (headers instanceof AxiosHeaders) headers.set(key, val);
  else (headers as any)[key] = val;
}

// Attach Firebase ID token when logged in + ensure JSON content-type on writes
api.interceptors.request.use(async (config) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const idToken = await user.getIdToken();
      if (!config.headers) config.headers = new AxiosHeaders();
      setHeader(config.headers as any, "Authorization", `Bearer ${idToken}`);
    }

    const method = String(config.method || "").toLowerCase();
    if (config.data && ["post", "put", "patch"].includes(method)) {
      if (!config.headers) config.headers = new AxiosHeaders();
      const hasCT =
        (config.headers instanceof AxiosHeaders &&
          (config.headers as AxiosHeaders).has("Content-Type")) ||
        (!(config.headers instanceof AxiosHeaders) &&
          (config.headers as any)["Content-Type"]);
      if (!hasCT) {
        setHeader(config.headers as any, "Content-Type", "application/json");
      }
    }
  } catch {
    /* no-op */
  }
  return config;
});

// Normalize proxy/HTML errors into clean Error messages
api.interceptors.response.use(
  (response) => {
    if (
      typeof response.data === "string" &&
      response.data.toLowerCase().includes("proxy error")
    ) {
      throw new Error("Proxy error from dev server to backend.");
    }
    return response;
  },
  (error: AxiosError<any>) => {
    const payload = error.response?.data as any;
    const msg =
      payload?.error ||
      payload?.message ||
      error.message ||
      "Network/Server error";
    return Promise.reject(new Error(msg));
  }
);

export default api;

/* ----------------------------------------------------------------------------
 * TYPES used by the owner dashboard
 * --------------------------------------------------------------------------*/

/** Full set of public profile fields your form can edit */
export type PublicProfilePayload = {
  enabled?: boolean;
  slug?: string;

  // branding / content
  tagline?: string;
  description?: string;
  logoUrl?: string;
  coverUrl?: string;

  // external website
  website?: string;

  // contact
  phonePublic?: string;

  // address
  addressStreet?: string;
  addressCity?: string;
  addressZip?: string;
  addressCountry?: string;

  // hours
  hoursText?: string;

  // socials
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  whatsapp?: string;

  // taxonomy / SEO
  categories?: string[];
  allowIndexing?: boolean;
};

export type MyBusiness = {
  id: string;
  name: string;
  website?: string;
  publicProfile?: PublicProfilePayload;
};

/* ----------------------------------------------------------------------------
 * Owner (authenticated) API helpers
 * --------------------------------------------------------------------------*/

export async function getMyBusiness(id: string): Promise<MyBusiness> {
  const res = await api.get(`/my-businesses/${id}`);
  const b = res.data || {};
  return {
    id: String(b.id ?? b._id),
    name: b.name ?? "",
    website: b.website ?? "",
    publicProfile: (b.publicProfile || {}) as PublicProfilePayload,
  };
}

export async function getMyBusinesses(): Promise<MyBusiness[]> {
  const res = await api.get("/my-businesses");
  const list = Array.isArray(res.data) ? res.data : [];
  return list.map((b: any) => ({
    id: String(b.id ?? b._id),
    name: b.name ?? "",
    website: b.website ?? "",
  }));
}

export async function updatePublicProfile(
  businessId: string,
  payload: PublicProfilePayload
): Promise<{ ok: boolean; publicProfile: PublicProfilePayload }> {
  const res = await api.put(`/my-businesses/${businessId}/publicProfile`, payload);
  return res.data;
}

/** NEW: upload to Cloudinary via backend */
export async function uploadPublicProfileImage(
  businessId: string,
  file: File,
  type: "logo" | "cover",
  onProgress?: (pct: number) => void
): Promise<{ url: string; field: "logoUrl" | "coverUrl" }> {
  const form = new FormData();
  form.append("file", file);
  form.append("businessId", businessId);
  form.append("type", type);

  const res = await api.post("/uploads/public-profile", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      onProgress(pct);
    },
  });
  return { url: res.data.url, field: res.data.field };
}




/* ----------------------------------------------------------------------------
 * Existing helpers kept as-is
 * --------------------------------------------------------------------------*/

export const getManualReviews = async () => {
  try {
    const res = await api.get("/my-business-reviews");
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
};

export const postReview = async (review: {
  author: string;
  content: string;
  rating: number;
  platform?: string;
}) => {
  const res = await api.post("/reviews", review);
  return res.data;
};

export const getSubscriptionStatus = async (): Promise<{
  subscription: { plan?: string; status?: string } | null;
  hasActiveSubscription: boolean;
}> => {
  const res = await api.get("/subscription/status");
  return res.data;
};

export const startTrial = async () => {
  const res = await api.post("/trial/start", {});
  return res.data;
};

export const getTrialStatus = async () => {
  const res = await api.get("/trial/status");
  return res.data;
};

export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  const res = await api.post("/stripe/create-portal-session", {});
  return res.data;
};

export const createCheckoutSession = async (
  priceId: string
): Promise<{ url: string }> => {
  const res = await api.post("/stripe/create-checkout-session", { priceId });
  return res.data;
};

export const createChangeSession = async (
  priceId: string
): Promise<{ url: string }> => {
  const res = await api.post("/stripe/create-change-session", { priceId });
  return res.data;
};

export const cancelSubscription = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const res = await api.post("/subscription/cancel", {});
  return res.data;
};

export const reactivateSubscription = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const res = await api.post("/subscription/reactivate", {});
  return res.data;
};

export const getBillingHistory = async (): Promise<{ invoices: any[] }> => {
  const res = await api.get("/billing/history");
  return res.data && Array.isArray(res.data.invoices)
    ? res.data
    : { invoices: [] };
};
